import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Plus, Wallet } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { tk, bn, fmtDate, todayStr } from "../lib/format";
import { useSettings } from "../context/SettingsContext";
import { PageHeader, Empty, Loader, Modal, Field } from "../components/kit";

export function CompanyForm({ onDone }: { onDone: (c: any) => void }) {
  const [f, setF] = useState({ name: "", mobile: "", address: "", opening_payable: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post("/companies", { ...f, opening_payable: +f.opening_payable || 0 });
      toast.success("কোম্পানি যোগ হয়েছে");
      onDone(data);
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="company-form">
      <Field label="কোম্পানির নাম *"><input className="inp" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="company-name-input" autoFocus /></Field>
      <Field label="মোবাইল / প্রতিনিধি"><input className="inp" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="company-mobile-input" /></Field>
      <Field label="ঠিকানা"><input className="inp" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="company-address-input" /></Field>
      <Field label="আগের দেনা (যদি থাকে)"><input className="inp num" type="number" min="0" step="any" value={f.opening_payable} onChange={(e) => setF({ ...f, opening_payable: e.target.value })} data-testid="company-opening-payable-input" /></Field>
      <button className="btn btn-primary w-full" disabled={busy} data-testid="company-form-submit">সেভ করুন</button>
    </form>
  );
}

function PayForm({ c, onDone }: { c: any; onDone: () => void }) {
  const { settings } = useSettings();
  const PAY_METHODS = settings.pay_methods;
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayStr());
  const [method, setMethod] = useState(PAY_METHODS[0]);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/companies/${c.id}/payments`, { amount: +amount, date, method });
      toast.success("কোম্পানিকে পরিশোধ সেভ হয়েছে");
      onDone();
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="company-pay-form">
      <p>বর্তমান দেনা: <b className="num text-red-600">{tk(c.payable)}</b></p>
      <Field label="পরিশোধের পরিমাণ (৳)"><input className="inp num" type="number" min="0" step="any" required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="company-pay-amount-input" autoFocus /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="তারিখ"><input className="inp" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="মাধ্যম"><select className="inp" value={method} onChange={(e) => setMethod(e.target.value)}>{PAY_METHODS.map((m) => <option key={m}>{m}</option>)}</select></Field>
      </div>
      <button className="btn btn-green w-full" disabled={busy} data-testid="company-pay-submit">পরিশোধ সেভ করুন</button>
    </form>
  );
}

function CompanyDetail({ id }: { id: string }) {
  const [d, setD] = useState<any>(null);
  useEffect(() => { api.get(`/companies/${id}`).then((r) => setD(r.data)); }, [id]);
  if (!d) return <Loader />;
  return (
    <div className="space-y-5" data-testid="company-detail">
      <div>
        <h4 className="font-semibold mb-2">ক্রয় ({bn(d.purchases.length)})</h4>
        {d.purchases.length === 0 ? <Empty /> : (
          <div className="overflow-x-auto"><table className="tbl"><thead><tr><th>তারিখ</th><th>চালান</th><th className="text-right">মোট</th><th className="text-right">পরিশোধ</th><th className="text-right">বাকি</th></tr></thead>
            <tbody>{d.purchases.map((p: any) => (<tr key={p.id}><td className="num">{fmtDate(p.date)}</td><td>{p.challan_no || p.purchase_no}</td><td className="text-right num">{tk(p.total)}</td><td className="text-right num text-emerald-700">{tk(p.paid)}</td><td className="text-right num text-red-600">{tk(p.due)}</td></tr>))}</tbody></table></div>
        )}
      </div>
      <div>
        <h4 className="font-semibold mb-2">পরিশোধ ({bn(d.payments.length)})</h4>
        {d.payments.length === 0 ? <Empty /> : (
          <div className="overflow-x-auto"><table className="tbl"><thead><tr><th>তারিখ</th><th>মাধ্যম</th><th className="text-right">পরিমাণ</th><th className="text-right">দেনা রইল</th></tr></thead>
            <tbody>{d.payments.map((p: any) => (<tr key={p.id}><td className="num">{fmtDate(p.date)}</td><td>{p.method}</td><td className="text-right num text-emerald-700">{tk(p.amount)}</td><td className="text-right num text-red-600">{tk(p.remaining_payable)}</td></tr>))}</tbody></table></div>
        )}
      </div>
    </div>
  );
}

export default function Companies() {
  const [list, setList] = useState<any[] | null>(null);
  const [add, setAdd] = useState(false);
  const [pay, setPay] = useState<any>(null);
  const [view, setView] = useState<any>(null);
  const load = useCallback(() => { api.get("/companies").then((r) => setList(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="কোম্পানি / ডিলার" subtitle="কোম্পানি অনুযায়ী ক্রয় ও দেনার হিসাব"
        actions={<button className="btn btn-primary" onClick={() => setAdd(true)} data-testid="companies-add-button"><Plus size={17} /> নতুন কোম্পানি</button>} />
      {!list ? <Loader /> : list.length === 0 ? <div className="card"><Empty text="এখনো কোনো কোম্পানি যোগ হয়নি" /></div> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="companies-list">
          {list.map((c) => (
            <div key={c.id} className="card p-5 fade-up" data-testid={`company-card-${c.id}`}>
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 grid place-items-center"><Building2 size={19} /></span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-lg text-slate-900 truncate">{c.name}</p>
                  <p className="text-xs text-slate-500">{bn(c.mobile)} {c.address}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center text-xs">
                <div className="rounded-lg bg-slate-50 p-2"><p className="text-slate-500">মোট ক্রয়</p><p className="num font-bold text-sm">{tk(c.total_purchase)}</p></div>
                <div className="rounded-lg bg-emerald-50 p-2"><p className="text-emerald-700">পরিশোধ</p><p className="num font-bold text-sm text-emerald-700">{tk(c.total_paid)}</p></div>
                <div className="rounded-lg bg-red-50 p-2"><p className="text-red-600">দেনা</p><p className="num font-bold text-sm text-red-600" data-testid={`company-payable-${c.id}`}>{tk(c.payable)}</p></div>
              </div>
              <div className="flex gap-2 mt-4">
                <button className="btn btn-ghost btn-sm flex-1" onClick={() => setView(c)} data-testid={`company-view-${c.id}`}>বিস্তারিত</button>
                <button className="btn btn-green btn-sm flex-1" onClick={() => setPay(c)} disabled={c.payable <= 0} data-testid={`company-pay-${c.id}`}><Wallet size={15} /> পরিশোধ</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal open={add} onClose={() => setAdd(false)} title="নতুন কোম্পানি" testid="company-add-modal"><CompanyForm onDone={() => { setAdd(false); load(); }} /></Modal>
      <Modal open={!!pay} onClose={() => setPay(null)} title={`পরিশোধ: ${pay?.name || ""}`} testid="company-pay-modal">{pay && <PayForm c={pay} onDone={() => { setPay(null); load(); }} />}</Modal>
      <Modal open={!!view} onClose={() => setView(null)} title={view?.name || ""} wide testid="company-detail-modal">{view && <CompanyDetail id={view.id} />}</Modal>
    </div>
  );
}
